import React, { useEffect, useRef, useState } from 'react';
import styles from '../Recursion.module.css';
import { RecursionCommands } from '../../../types/RecursionCommands';

const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

type Phase = 'unvisited' | 'calling' | 'done';

type TreeNode = {
  id: string;
  n: number;
  parentId: string | null;
  phase: Phase;
  result?: number;
  x: number;
  y: number;
  children: TreeNode[];
};

type Line = { key: string; x1: number; y1: number; x2: number; y2: number };

interface Props {
  command: RecursionCommands | null;
  onComplete: () => void;
  inputValue: string;
  setActiveLine: React.Dispatch<React.SetStateAction<number | null>>;
  isAnimating: boolean;
  setIsAnimating: React.Dispatch<React.SetStateAction<boolean>>;
  setFinalResult: React.Dispatch<React.SetStateAction<number | null>>;
  finalResult: number | null;
}

const FibonacciEngine: React.FC<Props> = ({
  command,
  onComplete,
  inputValue,
  setActiveLine,
  isAnimating,
  setIsAnimating,
  setFinalResult,
  finalResult,
}) => {
  const [nodes, setNodes] = useState<TreeNode[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [treeTransform, setTreeTransform] = useState({ x: 0, y: 0, scale: 1 });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const stopRef = useRef(false);
  
  useEffect(() => {
    if (command) executeCommand(command);
  }, [command]);

  const buildTree = (n: number, depth = 0, parentId: string | null = null): TreeNode => {
    const id = `node-${depth}-${n}-${Math.random().toString(36).slice(2)}`;
    const node: TreeNode = {
      id,
      n,
      parentId,
      phase: 'unvisited',
      x: 0,
      y: depth * 80,
      children: [],
    };
    if (n >= 2) {
      node.children.push(buildTree(n - 1, depth + 1, id));
      node.children.push(buildTree(n - 2, depth + 1, id));
    }
    return node;
  };

  const calculatePositions = (root: TreeNode) => {
    let nextX = 0;
    const sep = 80;

    const assignX = (node: TreeNode): { left: number[]; right: number[] } => {
      if (node.children.length === 0) {
        node.x = nextX;
        nextX += sep;
        return { left: [node.x], right: [node.x] };
      }
      const left = assignX(node.children[0]);
      let right = { left: [] as number[], right: [] as number[] };
      if (node.children.length > 1) {
        right = assignX(node.children[1]);
        const shift = calcShift(left.right, right.left);
        if (shift > 0) shiftSub(node.children[1], shift);
      }
      node.x = node.children.length === 1
        ? node.children[0].x
        : (node.children[0].x + node.children[1].x) / 2;
      return {
        left: [node.x, ...left.left],
        right: node.children.length > 1 ? [node.x, ...right.right] : [node.x, ...left.right],
      };
    };

    const calcShift = (r: number[], l: number[]) => {
      let max = 0;
      const len = Math.min(r.length, l.length);
      for (let i = 0; i < len; i++) {
        const need = r[i] - l[i] + sep;
        if (need > max) max = need;
      }
      return max;
    };

    const shiftSub = (n: TreeNode, s: number) => {
      n.x += s;
      n.children.forEach(c => shiftSub(c, s));
    };

    assignX(root);

    const minX = (function findMin(n: TreeNode): number {
      return Math.min(n.x, ...n.children.map(findMin));
    })(root);
    if (minX < 0) {
      (function shift(n: TreeNode) {
        n.x -= minX;
        n.children.forEach(shift);
      })(root);
    }
  };

  const flatten = (root: TreeNode) => {
    const outNodes: TreeNode[] = [];
    const outLines: Line[] = [];
    (function walk(n: TreeNode) {
      outNodes.push(n);
      n.children.forEach(c => {
        outLines.push({ key: `${n.id}-${c.id}`, x1: n.x, y1: n.y, x2: c.x, y2: c.y });
        walk(c);
      });
    })(root);
    return { outNodes, outLines };
  };

  const computeTransform = (ns: TreeNode[]) => {
    if (!containerRef.current || ns.length === 0) return { x: 0, y: 0, scale: 1 };
    const cw = containerRef.current.clientWidth;
    const ch = containerRef.current.clientHeight;
    console.log('Container dimensions:', cw, ch);
    const xs = ns.map(n => n.x);
    const ys = ns.map(n => n.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const maxY = Math.max(...ys);
    const treeW = maxX - minX + 80;
    const treeH = maxY + 80;
    const pad = 40;
    const scale = Math.min((cw - pad * 2) / treeW, (ch - pad * 2) / treeH, 1);
    const scaledW = treeW * scale;
    const offsetX = (cw - scaledW) / 2;
    return { x: offsetX, y: pad, scale };
  };

  const runAnimation = async (node: TreeNode): Promise<number> => {
    if (stopRef.current) throw new Error('stopped');
    setNodes(prev => prev.map(n => (n.id === node.id ? { ...n, phase: 'calling' } : n)));
    setActiveLine(0);
    await delay(500);

    if (node.n < 2) {
      setActiveLine(1);
      await delay(300);
      setNodes(prev => prev.map(n => (n.id === node.id ? { ...n, phase: 'done', result: node.n } : n)));
      return node.n;
    }

    setActiveLine(3);
    const left = await runAnimation(node.children[0]);
    if (stopRef.current) throw new Error('stopped');
    const right = await runAnimation(node.children[1]);
    if (stopRef.current) throw new Error('stopped');

    const res = left + right;
    setNodes(prev => prev.map(n => (n.id === node.id ? { ...n, phase: 'done', result: res } : n)));
    return res;
  };

  const reset = () => {
    stopRef.current = true;
    setNodes([]);
    setLines([]);
    setTreeTransform({ x: 0, y: 0, scale: 1 });
    setActiveLine(null);
  };

  const executeCommand = async (cmd: RecursionCommands) => {
    switch (cmd.action) {
      case 'run': {
        if (isAnimating) return;
        const n = parseInt(inputValue, 10);
        if (Number.isNaN(n) || n < 0 || n > 10) {
          alert('Please enter a number between 0 and 10.');
          return;
        }
        reset();
        stopRef.current = false;
        setIsAnimating(true);
        const root = buildTree(n);
        calculatePositions(root);
        const { outNodes, outLines } = flatten(root);
        setNodes(outNodes);
        setLines(outLines);
        setTreeTransform(computeTransform(outNodes));
        try {
          const result = await runAnimation(root);
          setFinalResult(result);
        } catch {
        } finally {
          setIsAnimating(false);
          setActiveLine(null);
          onComplete();
        }
        break;
      }
      case 'stop': {
        stopRef.current = true;
        setIsAnimating(false);
        onComplete();
        break;
      }
      case 'reset': {
        reset();
        setFinalResult(null);
        setIsAnimating(false);
        onComplete();
        break;
      }
      case 'generate': {
        break;
      }
    }
  };

  return (
    <div className={styles.treeVizContainer} ref={containerRef}>
      <div
        className={styles.treeNodeContainer}
        style={{
          transform: `translate(${treeTransform.x}px, ${treeTransform.y}px) scale(${treeTransform.scale})`,
          transformOrigin: 'top left',
        }}
      >
        <svg className={styles.treeLines}>
          {lines.map(line => (
            <line
              key={line.key}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              className={styles.treeLine}
            />
          ))}
        </svg>

        {nodes.map(node => (
          <div
            key={node.id}
            className={`${styles.treeNode} ${styles[node.phase]}`}
            style={{ left: `${node.x - 35}px`, top: `${node.y - 35}px` }}
          >
            <span className={styles.nodeLabel}>fib({node.n})</span>
            {node.phase === 'done' && (
              <span className={styles.treeNodeResult}>{node.result}</span>
            )}
          </div>
        ))}
      </div>

      {finalResult !== null && (
        <div className={styles.finalResultOverlay}>Result: {finalResult}</div>
      )}
    </div>
  );


};

export default FibonacciEngine;